
import React, { Component } from 'react';
import Select from 'react-select';
import { Form, Row, Col, Button, Modal } from 'react-bootstrap';
import ApiDataService from '../../services/ApiDataService';
import { WindowPanel } from '../../WindowPanel';
import Config from '../Config';
import * as XLSX from 'xlsx';
import MetadataGrid from './MetadataGrid';
import CommonGuideModal from '../CommonGuideModal/CommonGuideModal';

import { seoGuideTabs } from '../CommonGuideModal/guideData';

const API_URL = 'admin/portal/';

const customSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? '#007bff' 
      : state.isFocused 
      ? '#f8f9fa' 
      : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#333333',
    cursor: 'pointer',
    ':active': {
      backgroundColor: state.isSelected ? '#007bff' : '#e9ecef',
    }
  })
};

class Metadata extends Component {
  constructor(props) {
    super(props);
    this.state = {
      itemInfo: [],          // raw API rows
      selectedType: null,
      typeOptions: [],
      languageOptions: [],
      selectedLanguage: null,
      countryOptions: [],
      selectedCountry: null,
      importModalOpen: false,
      guideModalOpen: false,
      selectedFile: null,
      loading: false,
      currentPage: 1,
      totalRecords: 0,
      pageSize: 20,
    };
    this.gridRef = React.createRef();
  }

  componentDidMount() {
    this.fetchTypeOptions();
    this.fetchLanguageOptions();
    this.fetchCountryOptions();
  }

  fetchTypeOptions = () => {
    ApiDataService.get('admin/portal/seo/dropdown-master?channel_id=101-00000018')
      .then(response => {
        const options = response.data.result.map(item => ({
          value: item.PI_SYS_ID || item.PI_CODE,
          label: `${item.PI_PAGE_DESC}`,
          type: item.TYPE,
          code: item.PI_CODE,
          pi_sys_id: item.PI_SYS_ID
        }));
        this.setState({ typeOptions: options });
      })
      .catch(error => {
        console.error('Error fetching type options:', error);
      });
  };

  fetchLanguageOptions = () => {
    ApiDataService.get('admin/portal/seo/lang/lov?channel_id=101-00000018')
      .then(response => {
        const options = response.data.result.map(lang => ({
          value: lang.code,
          label: lang.desc
        }));
        this.setState({ languageOptions: options });
      })
      .catch(error => {
        console.error('Error fetching language options:', error);
      });
  };

  fetchCountryOptions = () => {
    ApiDataService.get('admin/portal/seo/country_lov?channel_id=101-00000018')
      .then(response => {
        const options = response.data.result.map(country => ({
          value: country.ref_cn_iso,
          label: country.code
        }));
        this.setState({ countryOptions: options });
      })
      .catch(error => {
        console.error('Error fetching country options:', error);
      });
  };

  fetchItemInfo = (type, language, country, id = '', code = '', page = 1, limit = 20) => {
    const typeParam = type ? `&type=${type}` : '';
    const langParam = language ? `&query_lang_code=${language}` : '';
    const countryParam = country ? `&country_code=${country}` : '';
    const idParam = id ? `&id=${id}` : '';
    const codeParam = code ? `&code=${code}` : '';
    const pageParam = `&page=${page}`;
    const limitParam = `&limit=${limit}`;
    this.setState({ loading: true, itemInfo: [] });
    ApiDataService.getAll(
      'admin/portal/seo/combined-list',
      `?channel_id=101-00000018${typeParam}${langParam}${countryParam}${idParam}${codeParam}${pageParam}${limitParam}`
    )
      .then(response => {
        this.setState({
          itemInfo: response?.data?.data || [],
          totalRecords: response?.data?.total || 0,
          currentPage: response?.data?.page || 1,
          pageSize: response?.data?.limit || 20,
          loading: false
        });
      })
      .catch(error => {
        console.error('Error fetching item info:', error);
        this.setState({ loading: false });
      });
  };

  handleTypeSelect = (selectedOption) => {
    const { selectedLanguage, selectedCountry } = this.state;
    this.setState({ selectedType: selectedOption, itemInfo: [], currentPage: 1 }, () => {
      if (selectedOption && selectedLanguage && selectedCountry) {
        this.fetchItemInfo(
          selectedOption.type,
          selectedLanguage.value,
          selectedCountry.value,
          selectedOption.pi_sys_id || '',
          selectedOption.code,
          1
        );
      }
    });
  };

  handleLanguageSelect = (selectedOption) => {
    this.setState({ selectedLanguage: selectedOption, itemInfo: [], currentPage: 1 }, () => {
      const { selectedType, selectedCountry } = this.state;
      if (selectedType && selectedOption && selectedCountry) {
        this.fetchItemInfo(
          selectedType.type,
          selectedOption.value,
          selectedCountry.value,
          selectedType.pi_sys_id || '',
          selectedType.code,
          1
        );
      }
    });
  };

  handleCountrySelect = (selectedOption) => {
    this.setState({ selectedCountry: selectedOption, itemInfo: [], currentPage: 1 }, () => {
      const { selectedType, selectedLanguage } = this.state;
      if (selectedType && selectedLanguage && selectedOption) {
        this.fetchItemInfo(
          selectedType.type,
          selectedLanguage.value,
          selectedOption.value,
          selectedType.pi_sys_id || '',
          selectedType.code,
          1
        );
      }
    });
  };

  // ─── Row Save Callback ────────────────────────────────────────────────────
  handleSaveRow = (fmData, seoId, seoFor, language, seoSysId, skipRefresh = false) => {
    const $url = seoSysId
      ? `${API_URL}seo/update/${seoSysId}?channel_id=101-00000018`
      : `${API_URL}seo?channel_id=101-00000018`;
    const msgType = seoSysId ? 'updated' : 'added';

    // Return the promise so the child component can track loading state
    return ApiDataService.post($url, fmData, language)
      .then(res => {
        if (res.data.return_status === '0') {
          Config.createNotification('success', `SEO data successfully ${msgType}.`);
          if (!skipRefresh) {
            const { selectedType, selectedLanguage, selectedCountry } = this.state;
            this.fetchItemInfo(
              selectedType?.type,
              selectedLanguage?.value,
              selectedCountry?.value,
              selectedType?.pi_sys_id || '',
              selectedType?.code,
              this.state.currentPage,
              this.state.pageSize
            );
          }
          return res.data;
        } else {
          const obj = res.data.result;
          if (obj) {
            const fields = [
              'seo_ref_id', 'seo_meta_title', 'seo_meta_tag', 'seo_meta_key_words',
              'seo_meta_desc', 'avatar', 'seo_og_title', 'seo_og_tag', 'seo_og_key_words',
              'seo_og_desc', 'seo_og_image_width', 'seo_og_image_height',
              'seo_twitter_title', 'seo_twitter_desc', 'seo_twitter_card',
              'seo_twitter_site', 'seo_active_yn'
            ];
            fields.forEach(f => {
              if (obj[f]) Config.createNotification('warning', obj[f]);
            });
          } else if (res.data.error_message) {
            Config.createNotification('error', res.data.error_message);
          }
          throw new Error(res.data.error_message || 'Validation failed');
        }
      })
      .catch(error => {
        if (error && error.message) {
          // Notification already handled in .then if it was a validation error
        } else if (error) {
          Config.createNotification('error', 'Network error or unexpected response');
        }
        throw error;
      });
  };

  handleDeleteRow = (seoId, seoSysId, language) => {
    const segment = seoSysId || '';
    // Based on ApiDataService.delete(path, id, params)
    return ApiDataService.delete(`${API_URL}seo/delete/`, segment, { lang_code: language })
      .then(res => {
        if (res.data.return_status === '0') {
          Config.createNotification('success', 'SEO data deleted successfully.');
          const { selectedType, selectedLanguage, selectedCountry } = this.state;
          this.fetchItemInfo(
            selectedType?.type,
            selectedLanguage?.value,
            selectedCountry?.value,
            selectedType?.value,
            selectedType?.code,
            this.state.currentPage,
            this.state.pageSize
          );
          return true;
        } else {
          Config.createNotification('error', res.data.error_message || 'Delete failed.');
          return false;
        }
      })
      .catch(error => {
        Config.createNotification('error', 'Error deleting data.');
        return false;
      });
  };

  // ─── Export / Import ──────────────────────────────────────────────────────
  handleExportClick = () => {
    if (this.gridRef.current) {
      this.gridRef.current.handleExport();
    } else {
      alert('Grid is not loaded yet.');
    }
  };
  handleExportAll = () => {
    const { selectedType, searchField, searchValue } = this.state;
    const type = selectedType?.type;
    if (!type) {
      Config.createNotification('error', 'Please select a type to export.');
      return;
    }

    const typeParam = `&type=${type}`;
    const idParam = selectedType?.value ? `&id=${selectedType.value}` : '';
    const codeParam = selectedType?.code ? `&code=${selectedType.code}` : '';
    const searchParam = searchValue ? `&search_field=${searchField}&search_value=${encodeURIComponent(searchValue)}` : '';

    this.setState({ loading: true });

    ApiDataService.getAll(
      'admin/portal/seo/export-all-list',
      `?channel_id=101-00000018${typeParam}${idParam}${codeParam}${searchParam}`
    )
      .then(response => {
        this.setState({ loading: false });
        if (response.data && response.data.status && response.data.data) {
          const rawData = response.data.data;
          const countriesList = response.data.countries || [];

          if (rawData.length === 0) {
            Config.createNotification('warning', 'No records found to export.');
            return;
          }

          // Map of internal keys to human-readable Excel headers
          const header_map = {
            'seo_ref_sys_id': 'SEO Ref Sys ID',
            'id': 'ID',
            'name': 'Name',
            'type': 'Type',
            'seo_sys_id': 'SEO ID',
            'seo_meta_title': 'Meta Title',
            'seo_page_name': 'Page Name',
            'seo_country': 'Country',
            'seo_meta_tag': 'Meta Tag',
            'seo_alt_tag': 'Alt Tag',
            'seo_meta_key_words': 'Meta Keywords',
            'seo_meta_desc': 'Meta Description',
            'seo_og_title': 'OG Title',
            'seo_og_tag': 'OG Tag',
            'seo_og_key_words': 'OG Keywords',
            'seo_og_type': 'OG Type',
            'seo_og_desc': 'OG Description',
            'seo_og_image_width': 'OG Img Width',
            'seo_og_image_height': 'OG Img Height',
            'seo_twitter_title': 'Twitter Title',
            'seo_twitter_desc': 'Twitter Desc',
            'seo_twitter_card': 'Twitter Card',
            'seo_twitter_site': 'Twitter Site',
            'seo_active_yn': 'Active',
            'lang_code': 'Lang',
            'seo_og_image_path': 'OG Img Main',
            'seo_og_image_path_01': 'OG Img 1',
            'seo_og_image_path_02': 'OG Img 2',
            'seo_og_image_path_03': 'OG Img 3',
            'seo_og_image_path_04': 'OG Img 4',
            'seo_og_image_path_05': 'OG Img 5',
          };

          const wb = XLSX.utils.book_new();

          countriesList.forEach(country => {
            const countryIso = country.SCN_ISO;
            const countryLabel = country.SCN_DESC || country.SCN_ISO; // e.g. UAE

            const countryRows = rawData.filter(row => row.seo_country === countryIso);
            if (countryRows.length === 0) {
              return;
            }

            // We want to sort/arrange rows such that PAGE_INFO is at the top of the sheet
            const pageInfoRows = countryRows.filter(row => row.SOURCE_TYPE === 'PAGE_INFO');
            const otherRows = countryRows.filter(row => row.SOURCE_TYPE !== 'PAGE_INFO');
            const sortedRows = [...pageInfoRows, ...otherRows];

            const excelRows = sortedRows.map(row => {
              const formattedRow = {};
              Object.keys(header_map).forEach(key => {
                formattedRow[header_map[key]] = row[key] !== undefined && row[key] !== null ? row[key] : '';
              });
              return formattedRow;
            });

            const ws = XLSX.utils.json_to_sheet(excelRows);
            XLSX.utils.book_append_sheet(wb, ws, countryLabel);
          });

          const fileName = `${type.toLowerCase()}_seo_metadata_${new Date().toISOString().slice(0, 10)}.xlsx`;
          XLSX.writeFile(wb, fileName);
          Config.createNotification('success', 'Excel file exported successfully.');
        } else {
          Config.createNotification('error', 'Failed to retrieve export data.');
        }
      })
      .catch(error => {
        this.setState({ loading: false });
        console.error('Error exporting SEO metadata:', error);
        Config.createNotification('error', 'Error exporting SEO metadata: ' + error.message);
      });
  };
  handleImportBtnClick = () => {
    this.setState({ importModalOpen: true });
  };

  handleGuideBtnClick = () => {
    this.setState({ guideModalOpen: true });
  };

  handleFileChange = (e) => {
    this.setState({ selectedFile: e.target.files[0] });
  };

  handleImportSubmit = () => {
    const { selectedFile } = this.state;
    if (!selectedFile) {
      alert('Please select an excel file first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];

      // Use sheet_to_json to correctly handle commas and special characters in cells
      const jsonData = XLSX.utils.sheet_to_json(ws, { defval: "" });
      this.processImportData(jsonData);
    };
    reader.readAsBinaryString(selectedFile);
  };

  processImportData = (jsonData) => {
    if (!jsonData || jsonData.length === 0) {
      alert('File is empty or could not be parsed.');
      return;
    }

    const browserData = localStorage.getItem('USER_INFO');
    const jsonAccess = browserData ? JSON.parse(browserData) : {};
    const authToken = localStorage.getItem('AUTH_TOKEN');

    // Get channel_id from the metadata menu info
    const menuUrl = window.location.pathname;
    const menuname = menuUrl.replace(/\//g, '').toUpperCase() + '_CHID';
    const menu = localStorage.getItem(menuname);
    const channelId = menu ? JSON.parse(menu).menucode : '101-00000018';

    const formData = new FormData();
    // Send the JSON data as a string in the 'key' parameter
    formData.append('key', JSON.stringify(jsonData));
    formData.append('excel_file', this.state.selectedFile);

    const selectedLang = this.state.selectedLanguage?.value || jsonAccess.lang_code || 'en';

    // Append all required COMMON_PARAMETER fields to FormData to ensure they are available in POST
    formData.append('lang_code', selectedLang);
    formData.append('logged_site_id', jsonAccess.site_id || '100001');
    formData.append('logged_user_id', jsonAccess.user_id || '');
    formData.append('channel_id', channelId);
    formData.append('auth_token', authToken || '');

    // Override the broken global Content-Type by setting it to undefined. 
    // This allows axios to automatically set the correct multipart/form-data header with boundary.
    const config = {
      headers: {
        'Content-Type': undefined
      }
    };

    ApiDataService.post('admin/portal/seo/bulkupload/excel?channel_id=101-00000018', formData, selectedLang, config)
      .then(response => {
        if (response.data.return_status === '0') {
          Config.createNotification('success', 'Metadata imported successfully.');
          this.setState({ importModalOpen: false, selectedFile: null });
          // Refresh grid
          const { selectedType, selectedLanguage, selectedCountry } = this.state;
          this.fetchItemInfo(
            selectedType?.type,
            selectedLanguage?.value,
            selectedCountry?.value,
            selectedType?.pi_sys_id || '',
            selectedType?.code,
            this.state.currentPage,
            this.state.pageSize
          );
        } else {
          const errMsg = response.data.error_message || 'Import failed.';
          Config.createNotification('error', errMsg);
          console.error('Backend validation error:', response.data);
        }
      })
      .catch(err => {
        // Log detailed error info if available in response
        if (err.response && err.response.data) {
          console.error('Server responded with error:', err.response.data);
          const msg = err.response.data.message || err.response.data.error_message || err.message;
          Config.createNotification('error', 'Import Error: ' + msg);
        } else {
          Config.createNotification('error', 'Error during import: ' + err.message);
        }
      });
  };


  render() {
    const {
      selectedType, typeOptions, selectedLanguage, languageOptions,
      selectedCountry, countryOptions, itemInfo, loading,
      importModalOpen
    } = this.state;

    const showGrid = !loading && itemInfo.length > 0;
    const noResults = !loading && selectedType && selectedLanguage && selectedCountry && itemInfo.length === 0;

    return (
      <WindowPanel rawHtml={
        <div className="windowContent">
          {/* Filter Bar */}
          <Row className="mb-3 align-items-end">
            <Col sm="2">
              <h5 style={{ fontSize: '14px' }}>Select</h5>
              <Select
                value={selectedType}
                onChange={this.handleTypeSelect}
                options={typeOptions}
                className="custdropdwn"
                placeholder="Select"
                menuPortalTarget={document.body}
                styles={customSelectStyles}
              />
            </Col>
            <Col sm="2">
              <h5 style={{ fontSize: '14px' }}>Select Language</h5>
              <Select
                value={selectedLanguage}
                onChange={this.handleLanguageSelect}
                options={languageOptions}
                className="custdropdwn"
                placeholder="Select Language"
                menuPortalTarget={document.body}
                styles={customSelectStyles}
              />
            </Col>
            <Col sm="2">
              <h5 style={{ fontSize: '14px' }}>Select Country</h5>
              <Select
                value={selectedCountry}
                onChange={this.handleCountrySelect}
                options={countryOptions}
                className="custdropdwn"
                placeholder="Select Country"
                menuPortalTarget={document.body}
                styles={customSelectStyles}
              />
            </Col>
            <Col sm="3" />
            <Col sm="3" className="d-flex align-items-end pb-1 justify-content-end">
              <Button
                variant="info"
                onClick={this.handleGuideBtnClick}
                style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 'bold', marginRight: '8px', color: '#fff', backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
              >
                📖 User Guide
              </Button>
              {showGrid && (
                <>
                  {/* <Button
                    variant="success"
                    onClick={this.handleExportAll}
                    style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 'bold', marginRight: '8px' }}
                  >
                    📊 Export All
                  </Button> */}
                  <Button
                    variant="primary"
                    onClick={this.handleImportBtnClick}
                    style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 'bold' }}
                  >
                    📤 Import
                  </Button>
                </>

              )}
            </Col>
          </Row>

          {/* Loading indicator */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#6c757d', fontSize: '14px' }}>
              <span>Loading data…</span>
            </div>
          )}

          {/* No results */}
          {noResults && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#adb5bd', fontSize: '13px' }}>
              No records found for the selected filters.
            </div>
          )}

          {/* Excel Grid */}
          {showGrid && (
            <MetadataGrid
              ref={this.gridRef}
              rows={itemInfo}
              selectedType={selectedType}
              selectedLanguage={selectedLanguage}
              selectedCountry={selectedCountry}
              currentPage={this.state.currentPage}
              totalRecords={this.state.totalRecords}
              pageSize={this.state.pageSize}
              onPageChange={(newPage) => {
                const { selectedType, selectedLanguage, selectedCountry } = this.state;
                if (selectedType) {
                  this.fetchItemInfo(
                    selectedType.type,
                    selectedLanguage?.value || '',
                    selectedCountry?.value || '',
                    selectedType.value,
                    selectedType.code,
                    newPage,
                    this.state.pageSize
                  );
                }
              }}
              onSaveRow={this.handleSaveRow}
              onDeleteRow={this.handleDeleteRow}
              onRefresh={() => {
                const { selectedType, selectedLanguage, selectedCountry } = this.state;
                if (selectedType) {
                  this.fetchItemInfo(
                    selectedType.type,
                    selectedLanguage?.value || '',
                    selectedCountry?.value || '',
                    selectedType.value,
                    selectedType.code,
                    this.state.currentPage,
                    this.state.pageSize
                  );
                }
              }}
            />
          )}

          {/* Import Modal */}
          <Modal
            show={importModalOpen}
            onHide={() => this.setState({ importModalOpen: false })}
          >
            <Modal.Header closeButton>
              <Modal.Title>Import ( Upload)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>Select Excel File</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={this.handleFileChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => this.setState({ importModalOpen: false })}
              >
                Close
              </Button>
              <Button variant="primary" onClick={this.handleImportSubmit}>
                Upload
              </Button>
            </Modal.Footer>
          </Modal>

          {/* User Guide Modal */}
          <CommonGuideModal
            show={this.state.guideModalOpen}
            onHide={() => this.setState({ guideModalOpen: false })}
            title="SEO Metadata User Guide & Tutorial"
            tabs={seoGuideTabs}
          />
        </div>
      } />
    );
  }
}

export default Metadata;
